import React, { Children, cloneElement, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Avatar from './Avatar';
import Tooltip from '../Tooltip';

/**
 * GroupContainer component is a custom component to wrap the avatars in a container.
 *
 * @param {node} children - The children of the group container.
 * @param {boolean} chained - Whether the avatars are chained.
 * @param {string} className - The class name of the group container.
 */
const GroupContainer = ({ children, chained, className }) => (
    <div
        className={classNames(
            'avatar-group',
            chained && 'avatar-group-chained',
            className
        )}
    >
        {children}
    </div>
);

/**
 * AvatarGroup component is a custom component to group avatars.
 *
 * @param {number} maxCount - The maximum count of avatars.
 * @param {boolean} chained - Whether the avatars are chained.
 * @param {string} className - The class name of the avatar group.
 * @param {boolean} omittedAvatarTooltip - Whether the omitted avatar has a tooltip.
 * @param {function} onOmittedAvatarClick - The function to be called when the omitted avatar is clicked.
 * @param {object} omittedAvatarProps - The props of the omitted avatar.
 * @param {string | node} omittedAvatarContent - The content of the omitted avatar.
 * @param {node} children - The children of the avatar group.
 * @returns {JSX.Element} - The AvatarGroup component.
 */
const AvatarGroup = (props) => {
    const {
        maxCount,
        chained,
        className,
        omittedAvatarTooltip,
        onOmittedAvatarClick,
        omittedAvatarProps,
        omittedAvatarContent,
        children,
    } = props;

    const childCount = Children.count(children);

    const childWithKey = Children.toArray(children).map((child, index) =>
        cloneElement(child, {
            key: `grouped-avatar-${index}`,
        })
    );

    if (maxCount && maxCount < childCount) {
        const childToShow = childWithKey.slice(0, maxCount);
        const overflowCount = childCount - maxCount;
        const avatar = (
            <Avatar
                className={onOmittedAvatarClick ? 'cursor-pointer' : ''}
                onClick={() => onOmittedAvatarClick?.()}
                {...omittedAvatarProps}
            >
                {omittedAvatarContent || `+${overflowCount}`}
            </Avatar>
        );

        childToShow.push(
            omittedAvatarTooltip ? (
                <Tooltip
                    key="avatar-more-tooltip"
                    title={`${overflowCount} More`}
                >
                    <>{avatar}</>
                </Tooltip>
            ) : (
                <Fragment key="avatar-more-tooltip">{avatar}</Fragment>
            )
        );
        return (
            <GroupContainer className={className} chained={chained}>
                {childToShow}
            </GroupContainer>
        );
    }

    return (
        <GroupContainer className={className} chained={chained}>
            {children}
        </GroupContainer>
    );
};

AvatarGroup.defaultProps = {
    maxCount: 3,
    omittedAvatarTooltip: false,
    chained: false,
};

Avatar.propTypes = {
    chained: PropTypes.bool,
    maxCount: PropTypes.number,
    omittedAvatarTooltip: PropTypes.bool,
    omittedAvatarProps: PropTypes.object,
    omittedAvatarContent: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node,
    ]),
};

export default AvatarGroup;
